import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDistanceToNow } from "date-fns";
import { useSafePolling } from "@/lib/useSafePolling";

export default function NotificationsPopover() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const audioRef = useRef(null);
  const prevUnread = useRef(0);

  const refresh = async (signal) => {
    try {
      const { data } = await api.get("/notifications", { signal });
      setItems(data.items || []);
      if ((data.unread || 0) > prevUnread.current && prevUnread.current > 0) {
        try { audioRef.current?.play(); } catch {}
        if ("Notification" in window && Notification.permission === "granted") {
          const latest = data.items?.[0];
          if (latest) new Notification(latest.title, { body: latest.body });
        }
      }
      prevUnread.current = data.unread || 0;
      setUnread(data.unread || 0);
    } catch {}
  };

  useSafePolling(refresh, 5000);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    refresh();
  };

  return (
    <>
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAA==" preload="auto" />
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" data-testid="notifications-trigger">
            <Bell className="h-5 w-5 text-slate-600" />
            {unread > 0 && (
              <Badge className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 flex items-center justify-center rounded-full bg-[#E0A96D] text-[#339999] text-[10px] font-bold border-2 border-white" data-testid="notifications-badge">
                {unread > 9 ? "9+" : unread}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <span className="font-semibold text-slate-900 text-sm">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-[#339999] hover:underline" data-testid="mark-all-read-btn">
                Mark all read
              </button>
            )}
          </div>
          <ScrollArea className="max-h-80">
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">No notifications yet</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {items.map((n) => (
                  <li key={n.notification_id} className={`px-4 py-3 ${!n.read ? "bg-blue-50/40" : ""}`}>
                    <div className="text-sm font-medium text-slate-900">{n.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{n.body}</div>
                    <div className="text-[10px] text-slate-400 mt-1">
                      {n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true }) : ""}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </>
  );
}
